// src/page/SummaryTicket.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function SummaryTicket() {
    useAuthGuard();

    // === spot & ticket selects (same style as EventEdit.jsx) ===
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);
    const spotOptions = useMemo(
        () => spots
            .map(s => ({
                value: s.spotId ?? s.id ?? s.spot_id ?? '',
                label: s.spotName ?? s.name ?? s.spot_name ?? ''
            }))
            .filter(o => o.value && o.label),
        [spots]
    );
    const [ticketId, setTicketId] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const ticketOptions = useMemo(
        () => tickets
            .map(t => ({ value: t.packageId, label: t.packageName }))
            .filter(o => o.value && o.label),
        [tickets]
    );

    // fetch spots once
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                setSpots(list);
            } catch {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // fetch tickets when spot changes
    useEffect(() => {
        if (!spotId) { setTickets([]); setTicketId(''); return; }
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingTickets(true);
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/ticket`);
                url.searchParams.set('size', '200');
                url.searchParams.set('sort', 'title');
                url.searchParams.set('dir', 'asc');
                url.searchParams.set('spotid', spotId);
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                setTickets(Array.isArray(data.items) ? data.items : []);
            } catch {
                setTickets([]);
            } finally {
                setLoadingTickets(false);
            }
        })();
        return () => ac.abort();
    }, [spotId]);

    // === flatpickr for 照会期間 ===
    const rangeRef = useRef(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    const rangeText = fromDate && toDate ? `${fmt(fromDate)} ~ ${fmt(toDate)}` : '';

    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range');
        if (!el) return;

        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromDate(dates[0]);
                    setToDate(dates[1]);
                } else if (!dates?.length) {
                    setFromDate('');
                    setToDate('');
                }
            }
        });
        rangeRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                    <li className="breadcrumb-item active">取得チケット</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">取得チケット</h2>
                    </div>
                </div>

                <div id="summary">
                    <div className="mb-4">
                        <form className="position-relative w-100" >
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <Select
                                        options={spotOptions}
                                        value={spotOptions.find(o => o.value === spotId) || null}
                                        onChange={(opt) => setSpotId(opt?.value || '')}
                                        isDisabled={loadingSpots}
                                        isClearable
                                        placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <Select
                                        options={ticketOptions}
                                        value={ticketOptions.find(o => o.value === ticketId) || null}
                                        onChange={(opt) => setTicketId(opt?.value || '')}
                                        isDisabled={!spotId || loadingTickets}
                                        isClearable
                                        placeholder={!spotId ? '' : (loadingTickets ? '読み込み中…' : '乗車券選択')}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div className="search-box">
                                    <label className="form-label mb-1 d-block">照会期間</label>
                                    <div className="position-relative">
                                        <input
                                            id="summary_period_range"
                                            className="form-control search-input search pe-5"
                                            type="text"
                                            name=""
                                            placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                            value={rangeText}
                                            readOnly
                                        />
                                        <span
                                            className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y"
                                            style={{ left: 12, pointerEvents: 'none' }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">購入済み</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">利用中</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">利用済み</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">期限切れ</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">キャンセル（利用済み）</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">キャンセル（未使用）</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" />
                                            <label className="form-check-label" htmlFor="status-withdrawn">取り消し</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary">クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" >
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3" role="button">スポット名<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">乗車券名<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">取得日時<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">利用日時<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">有効期限<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">ステータス<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">会員番号<span className="fas ms-1 text-muted fa-sort" /></th>
                                        <th className="align-middle ps-3" role="button">大人</th>
                                        <th className="align-middle ps-3" role="button">小人</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}

                    </div>
                </div>

            </div>
        </Layout>
    );
}
